import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { linksAPI } from '../lib/api';
import { AlertTriangle, Clock, Radio } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_345ce5ef-1c7a-4449-8db5-041edeaf083a/artifacts/srp7q3sr_LOGO%20EXSEG.png";

const StreamPage = () => {
  const { token } = useParams();
  const [streamData, setStreamData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState('');

  useEffect(() => {
    const fetchStream = async () => {
      try {
        const response = await linksAPI.getStream(token);
        setStreamData(response.data);
      } catch (err) {
        if (err.response?.status === 404) {
          setError('Link não encontrado ou inválido');
        } else if (err.response?.status === 410) {
          setError('Este link expirou');
        } else {
          setError('Erro ao carregar stream');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchStream();
  }, [token]);

  // Update countdown timer
  useEffect(() => {
    if (!streamData?.expires_at) return;

    const updateTimer = () => {
      const now = new Date();
      const expiry = new Date(streamData.expires_at);
      const diff = expiry - now;

      if (diff <= 0) {
        setTimeRemaining('Expirado');
        setError('Este link expirou');
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      if (days > 0) {
        setTimeRemaining(`${days}d ${hours}h ${minutes}m`);
      } else if (hours > 0) {
        setTimeRemaining(`${hours}h ${minutes}m ${seconds}s`);
      } else {
        setTimeRemaining(`${minutes}m ${seconds}s`);
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [streamData]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-8 h-8 border-3 border-gold/30 border-t-gold rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center p-4">
        <img src={LOGO_URL} alt="Gestao de RPAS" className="h-16 mb-8 drop-shadow-lg" />
        <div className="card-hud p-8 max-w-md w-full text-center">
          <AlertTriangle size={48} className="text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-rajdhani font-bold text-gray-900 uppercase mb-2">Acesso Negado</h1>
          <p className="text-gray-500 font-mono">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" data-testid="stream-page">
      <header className="sticky top-0 w-full bg-white/90 border-b border-gray-200 backdrop-blur-md z-10">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <img src={LOGO_URL} alt="Gestao de RPAS" className="h-10 drop-shadow" />
          
          <div className="flex items-center gap-4">
            <div className="live-indicator">
              <span className="live-dot"></span>
              <span className="text-alert-red text-xs">AO VIVO</span>
            </div>
            
            {streamData?.expires_at && timeRemaining && (
              <div className="flex items-center gap-2 text-ice-blue text-xs font-mono">
                <Clock size={14} />
                <span>{timeRemaining}</span>
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="bg-gray-100 px-4 py-2 border-b border-gray-200">
        <div className="container mx-auto flex items-center gap-2">
          <Radio size={16} className="text-gold" />
          <span className="text-gray-900 font-rajdhani font-semibold uppercase tracking-wider">{streamData?.name || 'Stream'}</span>
        </div>
      </div>

      {/* Stream Container */}
      <main className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-7xl aspect-video relative">
          <div className="absolute inset-0 card-hud overflow-hidden">
            <iframe
              src={streamData?.iframe_url}
              className="w-full h-full"
              frameBorder="0"
              allowFullScreen
              title={streamData?.name}
            />
            
            {/* HUD Corners */}
            <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-gold/50 pointer-events-none" />
            <div className="absolute top-0 right-0 w-8 h-8 border-t-2 border-r-2 border-gold/50 pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-8 h-8 border-b-2 border-l-2 border-gold/50 pointer-events-none" />
            <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-gold/50 pointer-events-none" />
            
            {/* REC indicator if applicable */}
            {streamData?.iframe_url?.includes('record') && (
              <div className="absolute top-4 left-4 flex items-center gap-2 bg-black/70 px-3 py-1 rounded-sm pointer-events-none">
                <Radio size={14} className="text-signal-green animate-pulse" />
                <span className="text-signal-green text-xs font-mono">REC</span>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-hud-black/50 border-t border-white/5 py-4">
        <div className="container mx-auto px-4 text-center">
          <p className="text-gray-500 text-xs font-mono">
            Sistema RPAS - Gestao de RPAS &copy; 2024
          </p>
        </div>
      </footer>
    </div>
  );
};

export default StreamPage;
